<template>
  <div class="ncrm-privacy-purposes d-flex flex-column ga-4">
    <div v-if="returnTo" class="d-flex">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" :to="returnTo">{{ $t(`${copyKey}.back`) }}</v-btn>
    </div>

    <v-skeleton-loader v-if="loading && !settings" type="card, table-heading, list-item-two-line@3" />
    <v-alert v-else-if="errorMessage" :type="forbidden ? 'warning' : 'error'">
      {{ errorMessage }}
      <template v-if="!forbidden" #append>
        <v-btn variant="text" @click="load">{{ $t(`${copyKey}.retry`) }}</v-btn>
      </template>
    </v-alert>
    <template v-else-if="settings">
      <NcrmPrivacySettingsCard :settings="settings" :can-manage="canManage" @saved="afterSave('settings')" />

      <div class="d-flex align-center justify-space-between flex-wrap ga-2">
        <div>
          <h3 class="text-subtitle-1 font-weight-bold">{{ $t(`${copyKey}.title`) }}</h3>
          <p class="text-body-2 text-medium-emphasis mb-0">{{ $t(`${copyKey}.subtitle`) }}</p>
        </div>
        <v-btn v-if="canManage" color="primary" prepend-icon="mdi-plus" @click="openDrawer(null)">{{ $t(`${copyKey}.new`) }}</v-btn>
      </div>
      <v-data-table
        :headers="headers"
        :items="purposes"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        :no-data-text="$t(`${copyKey}.empty`)"
        :row-props="() => ({ style: 'cursor: pointer' })"
        item-value="id"
        @click:row="(_, { item }) => openDrawer(item)"
      >
        <template #[`item.name`]="{ item }">
          <span class="font-weight-medium">{{ item.name }}</span>
          <div class="text-caption text-medium-emphasis">{{ item.code }}</div>
        </template>
        <template #[`item.category`]="{ item }">{{ $t(`ncrm.contacts.dataProtection.record.categories.${item.category}`) }}</template>
        <template #[`item.channels`]="{ item }">
          {{ (item.channels || []).map((channel) => $t(`ncrm.contacts.dataProtection.record.channels.${channel}`)).join(", ") }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="statusColors[item.status]">{{ $t(`${drawerKey}.status.${item.status}`) }}</v-chip>
        </template>
        <template #[`item.unknown_policy`]="{ item }">
          {{ $t(`${copyKey}.policies.${effectiveOf(item).value}`) }}
          <div class="text-caption text-medium-emphasis">{{ $t(`${drawerKey}.source.${effectiveOf(item).source}`) }}</div>
        </template>
        <template #[`item.double_opt_in_enabled`]="{ item }">
          {{ $t(`${copyKey}.${item.double_opt_in_enabled ? "on" : "off"}`) }}
        </template>
      </v-data-table>
    </template>

    <NcrmPrivacyPurposeDrawer v-model="drawerOpen" :purpose="selected" :settings="settings" :can-manage="canManage"
      @saved="afterSave" />
    <v-snackbar v-model="snackbar" color="success" :timeout="3500">{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import { hasPrivacyManagerRole } from "@/Utile/ncrmPrivacyAccess.mjs";
import NcrmPrivacyPurposeDrawer from "./NcrmPrivacyPurposeDrawer.vue";
import NcrmPrivacySettingsCard from "./NcrmPrivacySettingsCard.vue";
import { fetchPrivacySettings, fetchPurposeCatalog } from "./privacyPurposeCatalogApi.mjs";
import { effectivePolicy, safeReturnTo } from "./privacyPurposeModel.mjs";

const STATUS_ORDER = Object.freeze({ active: 0, draft: 1, retired: 2 });

export default {
  name: "NcrmPrivacyPurposesSection",
  components: { NcrmPrivacyPurposeDrawer, NcrmPrivacySettingsCard },
  data: () => ({
    loading: false, errorMessage: "", forbidden: false, settings: null, purposes: [], requestSequence: 0,
    canManage: false, drawerOpen: false, selected: null, snackbar: false, snackbarText: "",
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.purposes"; },
    drawerKey() { return `${this.copyKey}.drawer`; },
    statusColors() { return { active: "success", draft: "info", retired: undefined }; },
    returnTo() { return safeReturnTo(this.$route.query.returnTo); },
    headers() {
      return ["name", "category", "channels", "status", "unknown_policy", "double_opt_in_enabled"].map((key) => ({
        key, title: this.$t(`${this.copyKey}.columns.${key}`), sortable: false,
      }));
    },
  },
  created() {
    this.canManage = hasPrivacyManagerRole();
    this.load();
  },
  methods: {
    effectiveOf(purpose) { return effectivePolicy(purpose, this.settings); },
    async load() {
      const sequence = ++this.requestSequence;
      this.loading = true; this.errorMessage = ""; this.forbidden = false;
      try {
        const [settings, purposes] = await Promise.all([fetchPrivacySettings(), fetchPurposeCatalog()]);
        if (sequence !== this.requestSequence) return;
        this.settings = settings;
        // Activele primele, apoi drafturile; retrasele rămân vizibile pentru istoric.
        this.purposes = [...purposes].sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || a.name.localeCompare(b.name));
        this.openLinkedPurpose();
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.forbidden = Number(error?.response?.status) === 403;
        this.errorMessage = this.$t(`${this.copyKey}.${this.forbidden ? "noPermission" : "loadError"}`);
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    // Deep-link `?purpose=<id>`: linkul contextual deschide direct rândul relevant.
    openLinkedPurpose() {
      const id = Number(this.$route.query.purpose);
      const purpose = Number.isInteger(id) ? this.purposes.find((item) => item.id === id) : null;
      if (purpose && !this.drawerOpen) this.openDrawer(purpose);
    },
    openDrawer(purpose) {
      this.selected = purpose;
      this.drawerOpen = true;
    },
    // După o mutație reușită: drawer-ul s-a închis, datele se reîncarcă, feedback discret.
    afterSave(kind) {
      this.snackbarText = this.$t(`${this.copyKey}.saved.${kind}`);
      this.snackbar = true;
      if (this.$route.query.purpose) {
        const query = { ...this.$route.query };
        delete query.purpose;
        this.$router.replace({ query });
      }
      this.load();
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-purposes { text-align: left; }
</style>
